import type React from 'react';
type UseMediaPerformanceConfig = {
  frameRate?: number;
  mediaUrl?: string;
  mediaType: (typeof MEDIA_TYPE)[keyof typeof MEDIA_TYPE];
};
import { useEffect, useRef, useState } from 'react';
import type { MEDIA_TYPE } from '../../../entity-tagging/constants/constants';
import { useMetrics } from '../../../metrics/useMetrics';
import { useLogger } from '../../../modernization-common/hooks/use-logger';
import { useNetworkInfo } from '../use-network-info';
import { MEDIA_ERROR_CODE, MEDIA_ERROR_REASON, MEDIA_EVENTS, MEDIA_INFO, MEDIA_METRIC_EVENTS, NETWORK_METRICS } from './constants';
import { useMediaInfo } from './use-media-info';
type MediaPerformanceMetrics = {
  initialLoadTime: number | null;
  totalBufferingTime: number | null;
  error: keyof typeof MEDIA_ERROR_REASON | null;
};
export const useMediaPerformance = (ref: React.RefObject<HTMLVideoElement | HTMLAudioElement>, config: UseMediaPerformanceConfig, fetchFileSizeAndFormat?: (src: string) => Promise<{
  fileSize: number | null;
  format: string | null;
}>) => {
  const metricsTracker = useMetrics();
  const logger = useLogger();
  const networkMetric = useNetworkInfo();

  // eslint-disable-next-line react-hooks/refs
  const mediaInfo = useMediaInfo({
    mediaElement: ref.current,
    fetchFileSizeAndFormat
  });
  const [metrics, setMetrics] = useState<MediaPerformanceMetrics>({
    initialLoadTime: null,
    totalBufferingTime: null,
    error: null
  });
  const loadStartTimeRef = useRef<number | null>(null);
  const bufferingStartRef = useRef<number | null>(null);
  const totalBufferingTimeRef = useRef<number>(0);
  const seekingRef = useRef<boolean>(false);
  const lastMediaErrorDetailsRef = useRef<{
    code: number | null;
    message: string | null;
  } | null>(null);
  useEffect(() => {
    if (!networkMetric.downlink || mediaInfo.duration === null || !mediaInfo.format) {
      return;
    }
    metricsTracker.captureLatency(NETWORK_METRICS.DOWNLINK, networkMetric.downlink, {
      format: mediaInfo.format,
      fileSize: `${mediaInfo.fileSize}MB`,
      media_length: `${mediaInfo.duration}s`,
      mediaType: config.mediaType
    });
  }, [config.mediaType, networkMetric.downlink, metricsTracker, mediaInfo]);
  useEffect(() => {
    if (mediaInfo.fileSize) {
      metricsTracker.captureLatency(MEDIA_INFO.FILE_SIZE, mediaInfo.fileSize, {
        format: mediaInfo.format,
        media_length: `${mediaInfo.duration}s`,
        frame_rate: String(config.frameRate),
        mediaType: config.mediaType
      });
    }
  }, [config.frameRate, mediaInfo, metricsTracker, config.mediaType]);
  useEffect(() => {
    if (metrics.initialLoadTime && mediaInfo.fileSize && mediaInfo.format && mediaInfo.duration && config.mediaUrl) {
      const loadTimeEvent = config.mediaType === 'AUDIO' ? MEDIA_METRIC_EVENTS.AUDIO_LOAD_TIME : MEDIA_METRIC_EVENTS.VIDEO_LOAD_TIME;
      metricsTracker.captureLatency(loadTimeEvent, metrics.initialLoadTime, {
        fileSize: `${mediaInfo.fileSize}MB`,
        format: mediaInfo.format,
        media_length: `${mediaInfo.duration}s`,
        frame_rate: String(config.frameRate),
        mediaType: config.mediaType
      });
      metricsTracker.incrementCount(MEDIA_METRIC_EVENTS.LOAD_SUCCESS, {
        fileSize: `${mediaInfo.fileSize}MB`,
        format: mediaInfo.format,
        media_length: `${mediaInfo.duration}s`,
        frame_rate: String(config.frameRate),
        mediaType: config.mediaType
      });
    }
  }, [mediaInfo, metrics.initialLoadTime, metricsTracker, config.mediaUrl, config.frameRate, config.mediaType]);
  useEffect(() => {
    if (metrics.totalBufferingTime !== null && mediaInfo.fileSize && mediaInfo.format && mediaInfo.duration && config.mediaUrl) {
      const bufferingEvent = config.mediaType === 'AUDIO' ? MEDIA_METRIC_EVENTS.AUDIO_BUFFERING : MEDIA_METRIC_EVENTS.VIDEO_BUFFERING;
      metricsTracker.captureLatency(bufferingEvent, metrics.totalBufferingTime, {
        fileSize: `${mediaInfo.fileSize}MB`,
        format: mediaInfo.format,
        media_length: `${mediaInfo.duration}s`,
        frame_rate: String(config.frameRate),
        mediaType: config.mediaType
      });
      setMetrics(prev => ({
        ...prev,
        totalBufferingTime: null
      }));
    }
  }, [config.frameRate, config.mediaUrl, mediaInfo, metrics.totalBufferingTime, metricsTracker, config.mediaType]);
  useEffect(() => {
    if (metrics.error) {
      logger?.error('Failed to load media', {
        errorReason: metrics.error,
        errorCode: lastMediaErrorDetailsRef.current?.code ?? null,
        errorMessage: lastMediaErrorDetailsRef.current?.message ?? 'Unknown error',
        fileSize: `${mediaInfo?.fileSize}MB`,
        format: mediaInfo?.format,
        mediaLength: `${mediaInfo?.duration}s`,
        frameRate: config.frameRate,
        mediaType: config.mediaType
      });
      metricsTracker.incrementCount(MEDIA_METRIC_EVENTS.ERROR, {
        fileSize: `${mediaInfo?.fileSize}MB`,
        format: mediaInfo?.format,
        media_length: `${mediaInfo?.duration}s`,
        frame_rate: String(config.frameRate),
        error_code: metrics.error,
        mediaType: config.mediaType
      });
    }
  }, [config.frameRate, config.mediaUrl, mediaInfo, metricsTracker, metrics.error, config.mediaType, logger]);
  useEffect(() => {
    const mediaElement = ref.current;
    if (!mediaElement) return;
    const handleError = () => {
      const error = mediaElement.error;
      if (!error) return;
      const errorMessage = typeof error.message === 'string' ? error.message : null;
      let mappedReason: keyof typeof MEDIA_ERROR_REASON = MEDIA_ERROR_REASON.UNKNOWN;
      switch (error.code) {
        case MEDIA_ERROR_CODE.MEDIA_ERR_NETWORK:
          mappedReason = MEDIA_ERROR_REASON.NETWORK_ERROR;
          break;
        case MEDIA_ERROR_CODE.MEDIA_ERR_DECODE:
          mappedReason = MEDIA_ERROR_REASON.DECODE_ERROR;
          break;
        case MEDIA_ERROR_CODE.MEDIA_ERR_SRC_NOT_SUPPORTED:
          mappedReason = MEDIA_ERROR_REASON.COMPATIBILITY_FAILURE;
          break;
        default:
          break;
      }
      lastMediaErrorDetailsRef.current = {
        code: error.code,
        message: errorMessage
      };
      setMetrics(m => ({
        ...m,
        error: mappedReason
      }));
    };
    mediaElement.addEventListener(MEDIA_EVENTS.ERROR, handleError);
    return () => {
      mediaElement.removeEventListener(MEDIA_EVENTS.ERROR, handleError);
    };
  }, [ref]);
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && loadStartTimeRef.current !== null) {
        loadStartTimeRef.current = performance.now();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);
  useEffect(() => {
    const mediaElement = ref.current;
    if (!mediaElement) return;
    loadStartTimeRef.current = performance.now();
    const handleLoadedData = () => {
      if (document.visibilityState !== 'visible') {
        return;
      }
      if (loadStartTimeRef.current !== null) {
        const loadTime = performance.now() - loadStartTimeRef.current;
        loadStartTimeRef.current = null;
        setMetrics(prev => ({
          ...prev,
          initialLoadTime: loadTime
        }));
      }
    };
    mediaElement.addEventListener(MEDIA_EVENTS.LOADED_DATA, handleLoadedData);
    return () => {
      mediaElement.removeEventListener(MEDIA_EVENTS.LOADED_DATA, handleLoadedData);
    };
  }, [ref, metricsTracker]);
  useEffect(() => {
    const mediaElement = ref.current;
    if (!mediaElement) return;
    const handleWaiting = () => {
      // Only start buffering if not seeking
      if (!seekingRef.current) {
        bufferingStartRef.current = performance.now();
      }
    };
    const handlePlaying = () => {
      if (bufferingStartRef.current !== null && !seekingRef.current) {
        const bufferingTime = performance.now() - bufferingStartRef.current;
        totalBufferingTimeRef.current += bufferingTime;
        bufferingStartRef.current = null;
      }
      // Reset seeking flag after playing resumes
      seekingRef.current = false;
    };
    const handleSeeking = () => {
      seekingRef.current = true;
      // If buffering was started due to seeking, clear it
      bufferingStartRef.current = null;
    };
    const handleSeeked = () => {
      seekingRef.current = false;
    };
    const handleEnded = () => {
      setMetrics(prev => ({
        ...prev,
        totalBufferingTime: totalBufferingTimeRef.current
      }));
    };
    mediaElement.addEventListener(MEDIA_EVENTS.WAITING, handleWaiting);
    mediaElement.addEventListener(MEDIA_EVENTS.PLAYING, handlePlaying);
    mediaElement.addEventListener(MEDIA_EVENTS.SEEKING, handleSeeking);
    mediaElement.addEventListener(MEDIA_EVENTS.SEEKED, handleSeeked);
    mediaElement.addEventListener(MEDIA_EVENTS.ENDED, handleEnded);
    return () => {
      mediaElement.removeEventListener(MEDIA_EVENTS.WAITING, handleWaiting);
      mediaElement.removeEventListener(MEDIA_EVENTS.PLAYING, handlePlaying);
      mediaElement.removeEventListener(MEDIA_EVENTS.SEEKING, handleSeeking);
      mediaElement.removeEventListener(MEDIA_EVENTS.SEEKED, handleSeeked);
      mediaElement.removeEventListener(MEDIA_EVENTS.ENDED, handleEnded);
    };
  }, [ref, fetchFileSizeAndFormat]);
  return metrics;
};